import {
    Button,
    CssBaseline,
    StyledEngineProvider,
    ThemeProvider,
    Typography,
} from '@mui/material';
import CarMaxLogo from '@shared/components/CarMaxLogo';
import Footer from '@shared/components/Layout/components/Footer';
import useAuthService from '@shared/hooks/useAuthService';
import { clearMark, createMark, measure, PerformanceMarkName } from '@shared/hooks/usePerformance';
import AppLogger from '@shared/logging/appLogger';
import carmaxTheme from '@shared/theme/carmaxTheme';
import { useEffect, useState } from 'react';

import styles from './AuthHandler.module.scss';

// Delay before considered in error
const delay = 10000;

/**
 * This is a simple, non-network/MSAL aware component, used only for auth redirects.
 */
export default function AuthHandler() {
    const [isDelayed, setIsDelayed] = useState<boolean>(false);
    const { isAuthenticated, inProgress, getUser } = useAuthService();

    const displayText = isDelayed
        ? `It's taking longer than expected to load this page. Try
    returning to the Home page to resolve this issue.`
        : 'Signing in';

    // Show the return home button after 10 seconds
    useEffect(() => {
        const buttonTimeout = setTimeout(() => setIsDelayed(true), delay);
        return () => clearTimeout(buttonTimeout);
    }, []);

    useEffect(() => {
        if (isDelayed) {
            AppLogger.logException({
                error: new Error(
                    `MSAL AUTH_TAKING_TOO_LONG inProgress:${inProgress} isAuthenticated:${isAuthenticated()} user:${JSON.stringify(
                        getUser()
                    )}`
                ),
            });
            createMark(PerformanceMarkName.LoginComplete);
            measure(PerformanceMarkName.LoginStart, PerformanceMarkName.LoginComplete, {
                success: false,
            });
            clearMark(PerformanceMarkName.LoginStart);
            clearMark(PerformanceMarkName.LoginComplete);
        }
    }, [inProgress, isAuthenticated, getUser, isDelayed]);

    // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/FAQ.md#how-do-i-handle-the-redirect-flow-in-a-react-app

    return (
        <ThemeProvider theme={carmaxTheme}>
            <StyledEngineProvider injectFirst>
                <CssBaseline />
                <nav role="navigation" aria-label="main" className={styles['nav']}>
                    <div className={styles['nav--header']}>
                        <CarMaxLogo className={styles['logo']} />
                        <CarMaxLogo logoType="small-icon-only" className={styles['logo-small']} />
                    </div>
                </nav>
                <main className={styles['container']}>
                    <div className={styles['container--info']}>
                        <hzn-loading size="medium"></hzn-loading>
                        <Typography
                            variant="body1"
                            textAlign="center"
                            sx={{ margin: '16px 0' }}
                            className={styles['container--body']}>
                            {displayText}
                        </Typography>
                        {isDelayed && (
                            <Button
                                disableRipple
                                color="primary"
                                variant="contained"
                                className={styles['container--btn']}
                                sx={{ width: '250px', marginBottom: '64px' }}
                                fullWidth
                                href={`/`}>
                                RETURN TO HOME
                            </Button>
                        )}
                    </div>
                </main>
                <Footer></Footer>
            </StyledEngineProvider>
        </ThemeProvider>
    );
}
